import { useCallback, useEffect, useRef } from "react";

type TypingAwareAutoSaveOptions = {
  enabled: boolean | undefined;
  intervalMs: number;
  onAutoSave: (args: { isAutoSave: true }) => void | Promise<void>;
  getEventTarget?: () => Document | HTMLElement | null;
  events?: Array<keyof DocumentEventMap>;
};

export function useTypingAwareAutoSave({
  enabled,
  intervalMs,
  onAutoSave,
  getEventTarget,
  events = ["input", "keydown"],
}: TypingAwareAutoSaveOptions) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTypingAtRef = useRef<number>(0);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  const schedule = useCallback(() => {
    clearTimer();

    const now = Date.now();
    const targetAt = Math.max(lastTypingAtRef.current + intervalMs, now + intervalMs);
    const delay = Math.max(0, targetAt - now);

    timeoutRef.current = setTimeout(async () => {
      if (!enabled) return;

      const idleEnough = Date.now() - lastTypingAtRef.current >= intervalMs;
      if (!idleEnough) {
        schedule();
        return;
      }

      await onAutoSave({ isAutoSave: true });

      lastTypingAtRef.current = Date.now();
      schedule();
    }, delay);
  }, [clearTimer, enabled, intervalMs, onAutoSave]);

  const markTyping = useCallback(() => {
    lastTypingAtRef.current = Date.now();
    schedule();
  }, [schedule]);

  useEffect(() => {
    if (!enabled) {
      clearTimer();
      return;
    }

    if (!lastTypingAtRef.current) lastTypingAtRef.current = Date.now();
    schedule();

    const target = getEventTarget?.() ?? document;
    if (!target) return;

    const useCapture = true;

    for (const event of events) target.addEventListener(event as string, markTyping, useCapture);

    return () => {
      clearTimer();
      for (const event of events)
        target.removeEventListener(event as string, markTyping, useCapture);
    };
  }, [clearTimer, enabled, events, getEventTarget, markTyping, schedule]);

  return { markTyping };
}
